import cn from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { DiagnocoinLedgerEntryType } from '@/shared/api/protocol-ts/api/billing_new/dto_diagnocoin_new_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { formatLocalizedDateTime } from '@/shared/lib';
import { Button, Modal, Spinner } from '@/shared/ui';

import { billingModel } from '@/entities/billing';
import { ModalID, modalModel } from '@/entities/modal';

import { Filters } from '@/features/filters';

import { DiagnocoinHistoryFilter, i18n } from '../../config';
import { useDiagnocoinLedger } from '../../hooks';
import { getAvailableDelta } from '../../lib';

import styles from './DiagnocoinHistoryModal.module.scss';

export const DiagnocoinHistoryModal = () => {
  const intl = useIntl();
  const { formatMessage, formatNumber } = intl;
  const dispatch = useAppDispatch();

  const { visible } = useAppSelector(
    modalModel.selectors.selectDiagnocoinHistoryModal,
  );
  const account = useAppSelector(billingModel.selectors.selectAccount);

  const [filter, setFilter] = useState(DiagnocoinHistoryFilter.all);

  const { entries, isLoading, hasFailed, hasMore, loadMore } =
    useDiagnocoinLedger({
      accountID: account?.ID ?? '',
      filter,
      isOpen: visible,
    });

  const isReloading = isLoading && entries.length === 0;

  const handleClose = () => {
    dispatch(modalModel.actions.closeModal(ModalID.DiagnocoinHistoryModal));
  };

  return (
    <Modal
      data-component="diagnocoin-history-modal"
      isOpen={visible}
      onCancel={handleClose}
      hideFooter
      title={formatMessage({
        id: 'diagnocoins.history.title',
        defaultMessage: 'Diagnocoins transactions',
      })}
    >
      <div className={styles.container}>
        <Filters<DiagnocoinHistoryFilter>
          items={Object.values(DiagnocoinHistoryFilter).map((value) => ({
            value,
            label: formatMessage(i18n.filterLabels[value]),
          }))}
          value={filter}
          size="small"
          onChange={setFilter}
        />

        <div className={styles.rows}>
          {isReloading && (
            <div className={styles.loader}>
              <Spinner size={32} primary />
            </div>
          )}

          {entries.map((entry) => {
            const delta = getAvailableDelta(entry);

            return (
              <div key={entry.ID} className={styles.row}>
                <span className={cn(styles.date, 'p3')}>
                  {
                    formatLocalizedDateTime({
                      date: entry.EffectiveAt?.toDate(),
                      intl,
                    }).formattedDate
                  }
                </span>

                <span className="p2">
                  {formatMessage(
                    i18n.entryTypeLabels[entry.Type] ??
                      i18n.entryTypeLabels[
                        DiagnocoinLedgerEntryType
                          .DiagnocoinLedgerEntryTypeInvalidValue
                      ],
                  )}
                </span>

                <span
                  className={cn(
                    styles.amount,
                    'p2',
                    'bold',
                    delta > 0 && styles.credit,
                    delta < 0 && styles.debit,
                    delta === 0 && styles.neutral,
                  )}
                >
                  {delta === 0
                    ? '—'
                    : formatNumber(delta, { signDisplay: 'always' })}
                </span>

                <span className={cn(styles.balance, 'p3')}>
                  {Number(entry.AvailableAfter ?? 0)}
                </span>
              </div>
            );
          })}

          {entries.length === 0 && !isReloading && (
            <p className={cn(styles.empty, 'p2')}>
              {hasFailed
                ? formatMessage({
                    id: 'diagnocoins.history.failed',
                    defaultMessage:
                      'Could not load the transaction history. Please try again.',
                  })
                : formatMessage({
                    id: 'diagnocoins.history.empty',
                    defaultMessage: 'No transactions yet',
                  })}
            </p>
          )}
        </div>

        <div className={styles.footer}>
          {hasMore && (
            <Button
              size="medium"
              variant="tertiary"
              loading={isLoading}
              onClick={loadMore}
            >
              {formatMessage({
                id: 'global.loadMore',
                defaultMessage: 'Load more',
              })}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
